/**
 * BayEngageReviews.Placeholders
 *
 * Generic, config-driven widget placeholder insertion. Reads the store-specific
 * mappings from BayEngageReviews.Placeholders.Config and inserts the configured
 * placeholders into the DOM. Re-inserts placeholders after SPA navigation by
 * observing the document body for anchor re-renders.
 */

define('BayEngageReviews.Placeholders', [
    'BayEngageReviews.Placeholders.Config',
    'BayReviews.ProductData',
    'BayReviews.ScriptLoader'
], function (
    PlaceholdersConfig,
    ProductData,
    ScriptLoader
) {
    'use strict';

    function log(config, message, type) {
        if (!config || !config.debugMode || !window.console) {
            return;
        }
        var fn = window.console[type || 'log'];
        if (typeof fn === 'function') {
            fn('BayReviews: ' + message);
        }
    }

    function isHomePage() {
        var loc = window.location || {};
        var pathname = (loc.pathname || '').toLowerCase();
        var hash = (loc.hash || '').toLowerCase();

        var isRootPath = pathname === '/' ||
            pathname === '/shopping.ssp' ||
            pathname === '/scs/shopping.ssp' ||
            pathname === '/servlet/url';

        if (!isRootPath) {
            return false;
        }

        return !hash ||
            hash === '#' ||
            hash === '#/' ||
            hash === '#!/home' ||
            hash === '#/homepage';
    }

    function isProductDetailPage() {
        var loc = window.location || {};
        var pathname = (loc.pathname || '').toLowerCase();
        var hash = (loc.hash || '').toLowerCase();
        var search = (loc.search || '').toLowerCase();
        var combined = search + hash;

        if (pathname.indexOf('/product/') >= 0) {
            return true;
        }
        if (hash.indexOf('/product/') >= 0) {
            return true;
        }
        if (combined.indexOf('product=') >= 0) {
            return true;
        }

        if (document.querySelector('#product-details, .product-details, .product-detail, .product-views-full-details, [data-view="ProductDetails"]')) {
            return true;
        }

        return false;
    }

    function isProductListPage() {
        var loc = window.location || {};
        var pathname = (loc.pathname || '').toLowerCase();
        var hash = (loc.hash || '').toLowerCase();

        if (pathname.indexOf('/search') >= 0 || pathname.indexOf('/category/') >= 0) {
            return true;
        }
        if (hash.indexOf('/search') >= 0 || hash.indexOf('/category/') >= 0) {
            return true;
        }

        if (document.querySelector('.facets-item-cell-grid-title') ||
            document.querySelector('.facets-browse-category-facets')) {
            return true;
        }

        return false;
    }

    function matchesPages(pages) {
        if (!pages || !pages.length) {
            return true;
        }

        for (var i = 0; i < pages.length; i++) {
            var page = pages[i];
            if (page === 'all') {
                return true;
            }
            if (page === 'home' && isHomePage()) {
                return true;
            }
            if (page === 'pdp' && isProductDetailPage()) {
                return true;
            }
            if (page === 'plp' && isProductListPage()) {
                return true;
            }
        }

        return false;
    }

    function findAnchor(selector, fallbackSelectors) {
        if (selector) {
            var configured = document.querySelector(selector);
            if (configured) {
                return configured;
            }
        }

        if (!fallbackSelectors || !fallbackSelectors.length) {
            return null;
        }

        for (var i = 0; i < fallbackSelectors.length; i++) {
            var el = document.querySelector(fallbackSelectors[i]);
            if (el) {
                return el;
            }
        }

        return null;
    }

    function insertRelativeTo(anchor, element, position) {
        if (!anchor || !anchor.parentNode) {
            return false;
        }

        if (position === 'before') {
            anchor.parentNode.insertBefore(element, anchor);
        } else if (position === 'after') {
            anchor.parentNode.insertBefore(element, anchor.nextSibling);
        } else if (position === 'prepend') {
            anchor.insertBefore(element, anchor.firstChild);
        } else if (position === 'replace') {
            anchor.parentNode.replaceChild(element, anchor);
        } else {
            anchor.appendChild(element);
        }

        return true;
    }

    function buildPlaceholder(def, config) {
        var el = document.createElement('div');

        if (def.id) {
            el.id = def.id;
        }

        if (def.className) {
            el.className = def.className;
        }

        if (def.style) {
            el.style.cssText = def.style;
        }

        if (def.attrs) {
            for (var key in def.attrs) {
                if (Object.prototype.hasOwnProperty.call(def.attrs, key)) {
                    el.setAttribute(key, def.attrs[key]);
                }
            }
        }

        if (def.id === '_tbr_bdg' && config && config.badgeImageUrl) {
            el.innerHTML =
                '<img src="' + config.badgeImageUrl + '"' +
                ' alt="Review Badge"' +
                ' loading="lazy"' +
                ' width="120"' +
                ' height="60"' +
                ' style="max-width: 100%; height: auto;">';
        }

        return el;
    }

    /**
     * Extract the path portion of a URL. If the value is already a path, return
     * it unchanged. This keeps frontend identifiers consistent with the backend
     * response, which also returns URL paths.
     */
    function getPathFromUrl(url) {
        if (!url) {
            return '';
        }
        if (url.indexOf('://') === -1) {
            return url;
        }
        try {
            var a = document.createElement('a');
            a.href = url;
            return a.pathname || url;
        } catch (e) {
            return url;
        }
    }

    function findPlpProductIdentifiers(titleEl, itemSelector) {
        var result = {
            productId: '',
            productUrl: '',
            productName: ''
        };

        if (!titleEl) {
            return result;
        }

        var item = null;
        var className = itemSelector ? itemSelector.replace(/^\./, '') : '';

        if (itemSelector && typeof titleEl.closest === 'function') {
            item = titleEl.closest(itemSelector);
        } else if (itemSelector) {
            var el = titleEl;
            while (el && el !== document.body) {
                if (el.classList && el.classList.contains(className)) {
                    item = el;
                    break;
                }
                el = el.parentNode;
            }
        }

        // Home-page product grids (featured category CCT, etc.) use different
        // item containers than the PLP. Try common fallback selectors.
        if (!item && typeof titleEl.closest === 'function') {
            var fallbackSelectors = [
                '.facets-item-cell-grid',
                '.featuredcategorycct-item-container',
                '.facets-item-cell-grid-details'
            ];
            for (var s = 0; s < fallbackSelectors.length; s++) {
                item = titleEl.closest(fallbackSelectors[s]);
                if (item) {
                    break;
                }
            }
        }

        if (item) {
            result.productId = item.getAttribute('data-item-id') || '';

            // Some item containers (e.g. PDP related items) store the product
            // id on a child element like <div itemprop="itemListElement">.
            if (!result.productId) {
                var childWithId = item.querySelector('[data-item-id]');
                if (childWithId) {
                    result.productId = childWithId.getAttribute('data-item-id') || '';
                }
            }
        }

        // Fall back to extracting identifiers from the nearest product link.
        var searchRoot = item || titleEl;
        var parent = titleEl.parentNode;
        var roots = [];
        if (searchRoot) {
            roots.push(searchRoot);
        }
        if (parent && parent !== searchRoot) {
            roots.push(parent);
        }

        for (var r = 0; r < roots.length; r++) {
            var links = roots[r].querySelectorAll('a');
            for (var l = 0; l < links.length; l++) {
                var link = links[l];
                var href = link.getAttribute('href') || '';
                var linkText = (link.textContent || link.innerText || '')
                    .replace(/\s+/g, ' ')
                    .trim();

                // Some stores use /product/{id}/{slug}; extract the numeric id.
                var match = href.match(/\/product\/([^/]+)/i);
                if (match && !result.productId) {
                    result.productId = decodeURIComponent(match[1]);
                }

                // Use the first product link as the URL source.
                if (!result.productUrl) {
                    result.productUrl = getPathFromUrl(href);
                }

                // Use the first link that has visible text as the name source.
                // Image links usually have no text, so we keep looking.
                if (!result.productName && linkText) {
                    result.productName = linkText;
                }

                if (result.productId && result.productUrl && result.productName) {
                    break;
                }
            }
            if (result.productUrl && result.productName) {
                break;
            }
        }

        return result;
    }

    function hasPlaceholderForAnchor(anchor, def, position) {
        if (!anchor) {
            return false;
        }

        var sibling = null;
        if (position === 'after') {
            sibling = anchor.nextSibling;
        } else if (position === 'before') {
            sibling = anchor.previousSibling;
        }

        while (sibling && sibling.nodeType !== 1) {
            sibling = position === 'after' ? sibling.nextSibling : sibling.previousSibling;
        }

        return sibling && sibling.nodeType === 1 && sibling.id === def.id;
    }

    function renderEntry(entry, config, productData) {
        if (!matchesPages(entry.pages)) {
            return false;
        }

        var anchors = [];
        if (entry.multiple) {
            var nodeList = document.querySelectorAll(entry.selector);
            for (var i = 0; i < nodeList.length; i++) {
                anchors.push(nodeList[i]);
            }
        } else {
            var anchor = findAnchor(entry.selector, entry.fallbackSelectors);
            if (anchor) {
                anchors.push(anchor);
            }
        }

        if (!anchors.length) {
            log(config, 'anchor not found for ' + entry.area + ': ' + entry.selector, 'warn');
            return false;
        }

        var defs = entry.placeholders || (entry.placeholder ? [entry.placeholder] : []);
        var insertedAny = false;

        for (var a = 0; a < anchors.length; a++) {
            var anchor = anchors[a];

            for (var p = 0; p < defs.length; p++) {
                var def = defs[p];

                if (!entry.multiple && document.getElementById(def.id)) {
                    continue;
                }

                if (entry.multiple && hasPlaceholderForAnchor(anchor, def, entry.position)) {
                    continue;
                }

                var el = buildPlaceholder(def, config);

                if (entry.area === 'pdp_stat' || entry.area === 'pdp_main') {
                    if (productData && productData.productId) {
                        el.setAttribute('data-product-id', productData.productId);
                    }
                }

                if (entry.area === 'plp_grid_stat' || entry.area === 'pdp_related_items') {
                    var identifiers = findPlpProductIdentifiers(anchor, entry.itemSelector);
                    if (identifiers.productId) {
                        el.setAttribute('data-product-id', identifiers.productId);
                    }
                    if (identifiers.productUrl) {
                        el.setAttribute('data-product-url', identifiers.productUrl);
                    }
                    if (identifiers.productName) {
                        el.setAttribute('data-product-name', identifiers.productName);
                    }
                }

                if (insertRelativeTo(anchor, el, entry.position)) {
                    insertedAny = true;
                    log(config, 'inserted #' + def.id + ' for ' + entry.area);
                }
            }
        }

        return insertedAny;
    }

    /**
     * Fetch PDP review statistics and stamp them on the _tbr_stat placeholder.
     * Pre-populating the attributes forces the bundle's ProductStatisticsWidget
     * to use its data-attribute path instead of the buggy API-only path.
     */
    function applyPdpStatsToElement(statEl, rating, count) {
        if (!statEl) {
            return;
        }
        statEl.setAttribute('data-rating-value', String(rating || 0));
        statEl.setAttribute('data-review-count', String(count || 0));
    }

    function fetchAndSetPdpStats(config) {
        if (!config.beautifyrApiUrl) {
            return;
        }

        var statEl = document.getElementById('_tbr_stat');
        if (!statEl) {
            return;
        }

        var productId = statEl.getAttribute('data-product-id') || '';
        if (!productId) {
            var urlMatch = (window.location.pathname || '').match(/\/product\/(\d+)/i) ||
                           (window.location.search || '').match(/[?&]product=(\d+)/);
            productId = urlMatch ? urlMatch[1] : '';
        }
        if (!productId) {
            return;
        }

        // If we already have cached stats for this product, stamp them on the
        // current placeholder and skip the network call. This handles SPA
        // re-renders where the _tbr_stat element is replaced but the product
        // id is the same.
        var cached = pdpStatsCache[productId];
        if (cached) {
            applyPdpStatsToElement(statEl, cached.rating, cached.count);
            log(config, 'PDP stats applied from cache for ' + productId);
            return;
        }

        // Avoid duplicate in-flight requests when the body observer or
        // afterShowContent fires multiple times in quick succession.
        if (pdpStatsPending[productId]) {
            log(config, 'PDP stats request already pending for ' + productId);
            return;
        }

        if (statEl.getAttribute('data-rating-value') === null) {
            statEl.setAttribute('data-rating-value', '0');
        }
        if (statEl.getAttribute('data-review-count') === null) {
            statEl.setAttribute('data-review-count', '0');
        }

        var url = config.beautifyrApiUrl + '/api/v1/product/reviews/statistics/' + encodeURIComponent(productId);

        pdpStatsPending[productId] = true;

        var xhr = new XMLHttpRequest();
        xhr.open('GET', url, true);
        xhr.setRequestHeader('Accept', 'application/json');
        xhr.setRequestHeader('store-url', config.storeUrl);
        xhr.onreadystatechange = function () {
            if (xhr.readyState !== 4) {
                return;
            }
            pdpStatsPending[productId] = false;
            if (xhr.status >= 200 && xhr.status < 300) {
                try {
                    var response = JSON.parse(xhr.responseText);
                    var data = response && response.data ? response.data : {};
                    var analytics = data.analytics || {};
                    var rating = analytics.average_rating || 0;
                    var count = analytics.total || 0;
                    pdpStatsCache[productId] = { rating: rating, count: count };
                    applyPdpStatsToElement(statEl, rating, count);
                    log(config, 'set PDP stats ' + rating + ' / ' + count);
                } catch (err) {
                    log(config, 'failed to parse PDP stats response', 'warn');
                }
            } else {
                log(config, 'PDP stats request failed: ' + xhr.status, 'warn');
            }
        };
        xhr.onerror = function () {
            pdpStatsPending[productId] = false;
            log(config, 'PDP stats request network error', 'warn');
        };
        xhr.send();
    }

    /**
     * PDP review statistics cache. Prevents duplicate in-flight API calls and
     * lets us re-apply cached stats when the _tbr_stat placeholder is
     * re-created during SPA navigation or body re-renders.
     */
    var pdpStatsCache = {};
    var pdpStatsPending = {};

    /**
     * PLP aggregate statistics helpers.
     */
    var plpStatsState = {
        pending: false,
        queuedProducts: {
            ids: [],
            urls: [],
            names: []
        },
        scriptLoaded: false,
        statsReady: false,
        initCount: 0,
        maxInitCount: 3
    };

    function getUnfetchedPlpProducts() {
        var result = {
            ids: [],
            urls: [],
            names: []
        };
        var seenIds = {};
        var seenUrls = {};
        var seenNames = {};
        var placeholders = document.querySelectorAll('#_tbr_col_stat:not([data-fetched])');
        for (var i = 0; i < placeholders.length; i++) {
            var el = placeholders[i];
            var productId = el.getAttribute('data-product-id');
            var productUrl = el.getAttribute('data-product-url');
            var productName = el.getAttribute('data-product-name');

            if (productId && !seenIds[productId]) {
                result.ids.push(productId);
                seenIds[productId] = true;
            }
            if (productUrl && !seenUrls[productUrl]) {
                result.urls.push(productUrl);
                seenUrls[productUrl] = true;
            }
            if (productName && !seenNames[productName]) {
                result.names.push(productName);
                seenNames[productName] = true;
            }
        }
        return result;
    }

    function updatePlpPlaceholderStats(products, requestedProducts, config) {
        products = products || [];
        requestedProducts = requestedProducts || { ids: [], urls: [], names: [] };

        var placeholders = document.querySelectorAll('#_tbr_col_stat');
        var matchedKeys = {};

        for (var i = 0; i < products.length; i++) {
            var product = products[i];
            var platformProductId = product.platform_product_id || '';
            var productUrl = getPathFromUrl(product.product_url || product.url || '');
            var productName = (product.product_name || product.name || '').trim();
            var lowerProductName = productName.toLowerCase();

            for (var j = 0; j < placeholders.length; j++) {
                var el = placeholders[j];
                var elId = el.getAttribute('data-product-id') || '';
                var elUrl = getPathFromUrl(el.getAttribute('data-product-url') || '');
                var elName = (el.getAttribute('data-product-name') || '').trim();
                var lowerElName = elName.toLowerCase();

                var isMatch = false;
                if (platformProductId && elId && platformProductId === elId) {
                    isMatch = true;
                } else if (productUrl && elUrl && productUrl === elUrl) {
                    isMatch = true;
                } else if (productName && elName &&
                    (lowerElName.indexOf(lowerProductName) >= 0 || lowerProductName.indexOf(lowerElName) >= 0)) {
                    isMatch = true;
                }

                if (!isMatch) {
                    continue;
                }

                if (product.review_rating !== undefined && product.review_rating !== null) {
                    el.setAttribute('data-rating', String(product.review_rating));
                    el.setAttribute('data-rating-value', String(product.review_rating));
                }
                if (product.review_count !== undefined && product.review_count !== null) {
                    el.setAttribute('data-review-count', String(product.review_count));
                }
                el.setAttribute('data-fetched', 'true');

                if (platformProductId) {
                    matchedKeys['id:' + platformProductId] = true;
                }
                if (productUrl) {
                    matchedKeys['url:' + productUrl] = true;
                }
                if (productName) {
                    matchedKeys['name:' + productName] = true;
                }
            }
        }

        markPlpPlaceholdersFetched(requestedProducts.ids, 'data-product-id', matchedKeys, 'id:');
        markPlpPlaceholdersFetched(requestedProducts.urls, 'data-product-url', matchedKeys, 'url:');
        markPlpPlaceholdersFetched(requestedProducts.names, 'data-product-name', matchedKeys, 'name:');

        log(config, 'updated PLP stats for ' + products.length + ' products; ' +
            'requested ids=' + requestedProducts.ids.length +
            ' urls=' + requestedProducts.urls.length +
            ' names=' + requestedProducts.names.length);
    }

    function markPlpPlaceholdersFetched(values, attr, matchedKeys, keyPrefix) {
        for (var i = 0; i < values.length; i++) {
            var value = values[i];
            var normalizedValue = attr === 'data-product-url' ? getPathFromUrl(value) : value;
            if (matchedKeys[keyPrefix + normalizedValue]) {
                continue;
            }
            var placeholders = document.querySelectorAll(
                '#_tbr_col_stat[' + attr + '="' + value + '"]:not([data-fetched])'
            );
            for (var j = 0; j < placeholders.length; j++) {
                var el = placeholders[j];
                el.setAttribute('data-rating', '0');
                el.setAttribute('data-rating-value', '0');
                el.setAttribute('data-review-count', '0');
                el.setAttribute('data-fetched', 'true');
            }
        }
    }

    function fetchPlpReviewStats(config) {
        if (!config.beautifyrApiUrl) {
            log(config, 'beautifyrApiUrl not configured, skipping PLP stats fetch');
            return;
        }

        var products = getUnfetchedPlpProducts();
        if (!products.ids.length && !products.urls.length && !products.names.length) {
            return;
        }

        plpStatsState.statsReady = false;

        if (plpStatsState.pending) {
            products.ids.forEach(function (id) {
                if (plpStatsState.queuedProducts.ids.indexOf(id) === -1) {
                    plpStatsState.queuedProducts.ids.push(id);
                }
            });
            products.urls.forEach(function (url) {
                if (plpStatsState.queuedProducts.urls.indexOf(url) === -1) {
                    plpStatsState.queuedProducts.urls.push(url);
                }
            });
            products.names.forEach(function (name) {
                if (plpStatsState.queuedProducts.names.indexOf(name) === -1) {
                    plpStatsState.queuedProducts.names.push(name);
                }
            });
            log(config, 'PLP stats request pending, queued ids=' + products.ids.length +
                ' urls=' + products.urls.length + ' names=' + products.names.length);
            return;
        }

        plpStatsState.pending = true;
        var requestedProducts = {
            ids: products.ids.slice(),
            urls: products.urls.slice(),
            names: products.names.slice()
        };

        var url = config.beautifyrApiUrl + '/api/v1/product/aggregate/list-ratings';

        var xhr = new XMLHttpRequest();
        xhr.open('POST', url, true);
        xhr.setRequestHeader('Content-Type', 'application/json');
        xhr.setRequestHeader('Accept', 'application/json');
        xhr.setRequestHeader('store-url', config.storeUrl);
        xhr.onreadystatechange = function () {
            if (xhr.readyState !== 4) {
                return;
            }
            plpStatsState.pending = false;

            if (xhr.status >= 200 && xhr.status < 300) {
                try {
                    var response = JSON.parse(xhr.responseText);
                    var returnedProducts = response && response.data && response.data.products ? response.data.products : [];
                    updatePlpPlaceholderStats(returnedProducts, requestedProducts, config);
                    plpStatsState.statsReady = true;
                    tryInitializeCollectionWidgets(config);

                    if (plpStatsState.queuedProducts.ids.length ||
                        plpStatsState.queuedProducts.urls.length ||
                        plpStatsState.queuedProducts.names.length) {
                        fetchPlpReviewStats(config);
                    }
                } catch (err) {
                    log(config, 'failed to parse PLP stats response', 'warn');
                }
            } else {
                log(config, 'PLP stats request failed: ' + xhr.status, 'warn');
            }
        };
        xhr.onerror = function () {
            plpStatsState.pending = false;
            log(config, 'PLP stats request network error', 'warn');
        };

        log(config, 'fetching PLP stats for ids=' + products.ids.length +
            ' urls=' + products.urls.length + ' names=' + products.names.length);
        xhr.send(JSON.stringify({
            platform_product_ids: products.ids,
            product_urls: products.urls,
            product_names: products.names
        }));
    }

    function resolveCollectionStatsWidgetUrl(config) {
        var env = config.environment || 'prod';
        if (env === 'local' || env === 'dev') {
            return 'https://tbr.dev.bm247.net/d/tbr-product-collection-statistics-widget.min.js';
        }
        return 'https://sf-rv.bayengage.com/d/tbr-product-collection-statistics-widget.min.js';
    }

    function whenBundleReady(callback) {
        var bundleScript = document.getElementById('bayengage-reviews-script');
        if (bundleScript && bundleScript.getAttribute('data-loaded') === 'true') {
            callback();
            return;
        }

        var handler = function () {
            window.removeEventListener('bayreviews:bundleLoaded', handler);
            callback();
        };
        window.addEventListener('bayreviews:bundleLoaded', handler);
    }

    function loadCollectionStatsScript(config) {
        var scriptUrl = resolveCollectionStatsWidgetUrl(config);
        if (!scriptUrl) {
            return;
        }

        var scriptId = '_tbr_col_stat_script';
        if (document.getElementById(scriptId)) {
            plpStatsState.scriptLoaded = true;
            tryInitializeCollectionWidgets(config);
            return;
        }

        // Preserve the bundle's collection initializer before the standalone
        // PLP script potentially overwrites it.
        var bundleInit = window.initializeCollectionStatisticsWidgets;

        var script = document.createElement('script');
        script.id = scriptId;
        script.src = scriptUrl;
        script.async = true;

        script.onload = function () {
            var standaloneInit = window.initializeCollectionStatisticsWidgets;
            if (bundleInit !== standaloneInit) {
                window.initializeCollectionStatisticsWidgets = function () {
                    if (typeof bundleInit === 'function') {
                        try {
                            bundleInit();
                        } catch (e) {
                            log(config, 'bundle collection initializer failed', 'warn');
                        }
                    }
                    if (typeof standaloneInit === 'function') {
                        try {
                            standaloneInit();
                        } catch (e) {
                            log(config, 'standalone collection initializer failed', 'warn');
                        }
                    }
                };
            }
            plpStatsState.scriptLoaded = true;
            tryInitializeCollectionWidgets(config);
            log(config, 'collection statistics widget script loaded');
        };

        script.onerror = function () {
            log(config, 'failed to load collection statistics widget script', 'error');
        };

        var nonceEl = document.querySelector('script[nonce]');
        var nonce = nonceEl ? nonceEl.getAttribute('nonce') : '';
        if (nonce) {
            script.setAttribute('nonce', nonce);
        }

        var firstScript = document.scripts && document.scripts[0];
        if (firstScript && firstScript.parentNode) {
            firstScript.parentNode.insertBefore(script, firstScript);
        } else {
            document.head.appendChild(script);
        }

        log(config, 'injected collection statistics widget script');
    }

    function tryInitializeCollectionWidgets(config) {
        if (!plpStatsState.scriptLoaded || !plpStatsState.statsReady) {
            return;
        }

        if (plpStatsState.initCount >= plpStatsState.maxInitCount) {
            return;
        }

        var placeholders = document.querySelectorAll('#_tbr_col_stat');
        if (!placeholders.length) {
            return;
        }

        for (var i = 0; i < placeholders.length; i++) {
            var el = placeholders[i];
            el.innerHTML = '';
            el.removeAttribute('data-initialized');
        }

        if (window.initializeCollectionStatisticsWidgets && typeof window.initializeCollectionStatisticsWidgets === 'function') {
            try {
                window.initializeCollectionStatisticsWidgets();
                plpStatsState.initCount++;
                log(config, 'initialized collection statistics widgets (' + plpStatsState.initCount + '/' + plpStatsState.maxInitCount + ')');
            } catch (err) {
                log(config, 'failed to initialize collection statistics widgets', 'warn');
            }
        }
    }

    function maybeFetchAndLoadCollectionStats(config) {
        if (!document.querySelector('#_tbr_col_stat')) {
            return;
        }

        fetchPlpReviewStats(config);

        whenBundleReady(function () {
            loadCollectionStatsScript(config);
        });
    }

    function renderPdpEntries(config, payload, pdp) {
        var productData = null;
        if (pdp) {
            productData = ProductData.getPdpProductData(pdp, config);
            if (productData) {
                ProductData.applyProductData(payload, productData);
            }
        }

        var inserted = false;
        for (var i = 0; i < PlaceholdersConfig.length; i++) {
            var entry = PlaceholdersConfig[i];
            if (entry.area === 'pdp_stat' || entry.area === 'pdp_main' || entry.area === 'pdp_related_items') {
                if (renderEntry(entry, config, productData)) {
                    inserted = true;
                }
            }
        }

        if (inserted) {
            fetchAndSetPdpStats(config);
            ScriptLoader.load(payload, {
                debugMode: config.debugMode,
                containerId: config.containerId
            });
        }

        maybeFetchAndLoadCollectionStats(config);
    }

    function renderPlpEntries(config, payload) {
        var inserted = false;
        for (var i = 0; i < PlaceholdersConfig.length; i++) {
            var entry = PlaceholdersConfig[i];
            if (entry.area === 'plp_grid_stat') {
                if (renderEntry(entry, config, null)) {
                    inserted = true;
                }
            }
        }

        if (inserted) {
            ScriptLoader.load(payload, {
                debugMode: config.debugMode,
                containerId: config.containerId
            });
        }

        maybeFetchAndLoadCollectionStats(config);
    }

    function renderAllEntries(config, payload, pdp) {
        var productData = null;
        if (isProductDetailPage() && pdp) {
            productData = ProductData.getPdpProductData(pdp, config);
            if (productData) {
                ProductData.applyProductData(payload, productData);
            }
        }

        var inserted = false;
        for (var i = 0; i < PlaceholdersConfig.length; i++) {
            if (renderEntry(PlaceholdersConfig[i], config, productData)) {
                inserted = true;
            }
        }

        if (inserted) {
            ScriptLoader.load(payload, {
                debugMode: config.debugMode,
                containerId: config.containerId
            });
        }

        if (isProductDetailPage()) {
            fetchAndSetPdpStats(config);
        }

        maybeFetchAndLoadCollectionStats(config);
    }

    function injectEmbedStyles() {
        var styleId = 'bayengage-embed-form-styles';
        if (document.getElementById(styleId)) {
            return;
        }

        var css =
            '.bayengage-embed-form #u_column_1 { width: 250px; }\n' +
            '.bayengage-embed-form { display: table !important; }';

        var style = document.createElement('style');
        style.id = styleId;
        style.type = 'text/css';

        if (style.styleSheet) {
            style.styleSheet.cssText = css;
        } else {
            style.appendChild(document.createTextNode(css));
        }

        var nonceEl = document.querySelector('script[nonce]');
        var nonce = nonceEl ? nonceEl.getAttribute('nonce') : '';
        if (nonce) {
            style.setAttribute('nonce', nonce);
        }

        var head = document.head || document.getElementsByTagName('head')[0];
        if (head) {
            head.appendChild(style);
        }
    }

    return {
        mount: function (config, payload, pdp, plp) {
            // Inject embed-form styles directly so they do not depend on SCSS
            // compilation timing or cache invalidation.
            injectEmbedStyles();

            // Initial render.
            renderAllEntries(config, payload, pdp);

            // Re-run PDP entries whenever the PDP content is shown (handles
            // matrix variant changes and SPA navigation to another product).
            if (pdp && typeof pdp.on === 'function') {
                pdp.on('afterShowContent', function () {
                    renderPdpEntries(config, payload, pdp);
                });
            }

            // Re-run PLP entries whenever PLP content is shown (pagination,
            // facet filtering, etc.). Infinite scroll is handled by the body
            // observer below.
            if (plp && typeof plp.on === 'function') {
                plp.on('afterShowContent', function () {
                    renderPlpEntries(config, payload);
                });
            }

            // Watch the whole body so placeholders are re-inserted whenever the
            // theme re-renders the anchor elements during SPA navigation.
            var bodyObserver = new MutationObserver(function () {
                renderAllEntries(config, payload, pdp);
            });

            if (document.body) {
                bodyObserver.observe(document.body, { childList: true, subtree: true });
            }
        }
    };
});
